import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
  Button,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';

import {
  assignQueuePattern,
  fetchQueuePatternAssignment,
  QueuePatternAssignment,
  QueueServicePattern,
} from '../../api/queuePatterns';

interface QueuePatternAssignmentDialogProps {
  open: boolean;
  entityId: string | null;
  entityName?: string;
  entityType: 'operator' | 'workstation';
  patterns: QueueServicePattern[];
  onClose: () => void;
  onAssigned?: (assignment: QueuePatternAssignment | null) => void;
}

const QueuePatternAssignmentDialog: React.FC<QueuePatternAssignmentDialogProps> = ({
  open,
  entityId,
  entityName,
  entityType,
  patterns,
  onClose,
  onAssigned,
}) => {
  const [selectedPatternId, setSelectedPatternId] = useState<string>('');
  const [autoReset, setAutoReset] = useState<boolean>(true);
  const [validationError, setValidationError] = useState<string>('');

  const assignmentQuery = useQuery(
    ['queue-pattern-assignment', entityType, entityId],
    () =>
      entityId
        ? fetchQueuePatternAssignment(
            entityType === 'operator' ? { operatorId: entityId } : { workstationId: entityId },
          )
        : Promise.resolve(null),
    {
      enabled: open && Boolean(entityId),
      staleTime: 10_000,
    },
  );

  const assignMutation = useMutation(assignQueuePattern, {
    onSuccess: (assignment) => {
      toast.success('Шаблон назначен');
      onAssigned?.(assignment);
      onClose();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Не удалось назначить шаблон');
    },
  });

  useEffect(() => {
    if (!open) {
      setSelectedPatternId('');
      setAutoReset(true);
      setValidationError('');
      return;
    }

    const currentAssignment = assignmentQuery.data;
    if (currentAssignment) {
      setSelectedPatternId(currentAssignment.patternId);
      setAutoReset(currentAssignment.autoReset);
    } else {
      setSelectedPatternId('');
      setAutoReset(true);
    }
    setValidationError('');
  }, [open, assignmentQuery.data]);

  const patternsMap = useMemo(() => {
    const map = new Map<string, QueueServicePattern>();
    patterns.forEach((pattern) => {
      map.set(pattern.id, pattern);
    });
    return map;
  }, [patterns]);

  const handleSave = async () => {
    if (!entityId) {
      return;
    }

    if (!selectedPatternId) {
      setValidationError('Выберите шаблон обслуживания');
      return;
    }

    setValidationError('');

    await assignMutation.mutateAsync({
      id: selectedPatternId,
      payload:
        entityType === 'operator'
          ? { operatorId: entityId, autoReset }
          : { workstationId: entityId, autoReset },
    });
  };

  const renderContent = () => {
    if (!entityId) {
      return <Typography color="text.secondary">Выберите сущность для назначения шаблона.</Typography>;
    }

    if (assignmentQuery.isLoading) {
      return (
        <Stack alignItems="center" spacing={2} py={4}>
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary">
            Загрузка текущего назначения…
          </Typography>
        </Stack>
      );
    }

    if (patterns.length === 0) {
      return (
        <Typography color="text.secondary">
          Нет созданных шаблонов. Сначала создайте шаблон в разделе «Шаблоны обслуживания».
        </Typography>
      );
    }

    const currentPattern = selectedPatternId ? patternsMap.get(selectedPatternId) : undefined;

    return (
      <Stack spacing={3}>
        <FormControl fullWidth error={Boolean(validationError)}>
          <InputLabel id="queue-pattern-select">Шаблон обслуживания</InputLabel>
          <Select
            labelId="queue-pattern-select"
            label="Шаблон обслуживания"
            value={selectedPatternId}
            onChange={(event) => setSelectedPatternId(event.target.value as string)}
          >
            {patterns.map((pattern) => (
              <MenuItem key={pattern.id} value={pattern.id}>
                {pattern.name}
                {!pattern.isActive ? ' (неактивен)' : ''}
              </MenuItem>
            ))}
          </Select>
          {validationError && <FormHelperText>{validationError}</FormHelperText>}
        </FormControl>

        <FormControlLabel
          control={<Switch checked={autoReset} onChange={(event) => setAutoReset(event.target.checked)} />}
          label="Автоматически переходить к следующему шагу"
        />

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Последовательность очередей
          </Typography>
          {currentPattern ? (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {currentPattern.sequence.map((queueId, index) => (
                <Chip key={`${queueId}-${index}`} label={`${index + 1}. ${queueId}`} size="small" />
              ))}
            </Stack>
          ) : (
            <Typography color="text.secondary">Выберите шаблон, чтобы увидеть последовательность.</Typography>
          )}
        </Box>

        {assignmentQuery.data && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Текущий прогресс
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Позиция: {assignmentQuery.data.positionPointer + 1}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Обновлено: {new Date(assignmentQuery.data.updatedAt).toLocaleString('ru-RU')}
            </Typography>
          </Box>
        )}
      </Stack>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Назначение шаблона
        {entityName ? (
          <Typography variant="body2" color="text.secondary">
            {entityName}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent dividers>{renderContent()}</DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Отмена
        </Button>
        <LoadingButton
          onClick={handleSave}
          variant="contained"
          loading={assignMutation.isLoading}
          disabled={!entityId || patterns.length === 0}
        >
          Сохранить
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default QueuePatternAssignmentDialog;
